import React, { useState, useEffect } from "react";
import DefaultButton from "../../../../../components/buttons/defaultButton";
import DefaultBadge from "../../../../../components/badge/defaultBadge";
import CommonTable from "../../../../../components/table/table";
import SalaryAssignWizard from "../../components/SalaryAssignWizard";
import SalaryHistoryModal from "../../components/SalaryHistoryModal";
import SalaryIncrementHistory from "../../components/SalaryIncrementHistory";
import AuditLogViewer from "../../components/AuditLogViewer";
import { HiCurrencyRupee, HiClock, HiEye, HiTrendingUp } from "react-icons/hi";
import moment from "moment";
import { getCurrentSalary, getSalaryHistory } from "../../../../../../services/workspace/salaryStructure.service";
import toast from "react-hot-toast";

const SalaryCard = ({ staff, loading, fetchData, heandleFetchData }) => {
  const [showWizard, setShowWizard] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showIncrementHistory, setShowIncrementHistory] = useState(false);
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [currentSalary, setCurrentSalary] = useState(null);
  const [salaryHistory, setSalaryHistory] = useState([]);
  const [loadingSalary, setLoadingSalary] = useState(false);

  // Fetch current salary and history
  useEffect(() => {
    const fetchSalaryData = async () => {
      if (!staff?.uuid) return;
      
      setLoadingSalary(true);
      try {
        // Fetch current salary
        const currentSalaryData = await getCurrentSalary(staff.uuid);
        if (currentSalaryData) {
          // Transform API response to match component format
          setCurrentSalary({
            id: currentSalaryData.id || currentSalaryData.uuid,
            structureName: currentSalaryData.structure_name || currentSalaryData.structureName || currentSalaryData.salary_structure?.name || "N/A",
            effectiveDate: currentSalaryData.effective_date || currentSalaryData.effectiveDate,
            status: currentSalaryData.status || "active",
            grossPay: currentSalaryData.gross_pay || currentSalaryData.grossPay || 0,
            netPay: currentSalaryData.net_pay || currentSalaryData.netPay || 0,
            ctc: currentSalaryData.annual_ctc || currentSalaryData.ctc || currentSalaryData.annualCTC || 0,
            createdBy: currentSalaryData.created_by?.name || currentSalaryData.createdBy || "System",
            createdAt: currentSalaryData.created_at || currentSalaryData.createdAt,
          });
        } else {
          setCurrentSalary(null);
        }

        // Fetch salary history
        const historyResponse = await getSalaryHistory(staff.uuid, { per_page: 50 });
        const historyData = historyResponse?.data || historyResponse || [];
        setSalaryHistory(historyData);
      } catch (error) {
        console.error("Failed to fetch salary data:", error);
        // Don't show error toast if it's just 404 (no salary assigned yet)
        if (error?.response?.status !== 404) {
          toast.error("Failed to fetch salary information");
        }
        setCurrentSalary(null);
        setSalaryHistory([]);
      } finally {
        setLoadingSalary(false);
    }
    };

    fetchSalaryData();
  }, [staff?.uuid]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return moment(dateString).format("DD MMM YYYY");
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return <DefaultBadge status={1} custom="Active" />;
      case "upcoming":
        return <DefaultBadge status={0} custom="Upcoming" />;
      case "expired":
        return <DefaultBadge status={2} custom="Expired" />;
      default:
        return <DefaultBadge status={0} custom="Pending" />;
    }
  };

  const TableHeaderData = [
    { label: "Effective Date", key: "effectiveDate" },
    { label: "Structure Name", key: "structureName" },
    { label: "Created By", key: "createdBy" },
    { label: "Created At", key: "createdAt" },
    { label: "Status", key: "status" },
    { label: "Actions", key: "actions" },
  ];

  const tableData = salaryHistory.map((record) => ({
    id: { data: record.id || record.uuid, variant: "text" },
    effectiveDate: {
      data: formatDate(record.effective_date || record.effectiveDate),
      variant: "text",
    },
    structureName: { 
      data: record.structure_name || record.structureName || record.salary_structure?.name || "N/A", 
      variant: "text" 
    },
    createdBy: { 
      data: record.created_by?.name || record.createdBy || "System", 
      variant: "text" 
    },
    createdAt: {
      data: moment(record.created_at || record.createdAt).format("DD MMM YYYY, hh:mm A"),
      variant: "text",
    },
    status: {
      data: record.status === "active" ? 1 : record.status === "upcoming" ? 0 : 2,
      variant: "status",
      customCases: {
        1: "Active",
        0: "Upcoming",
        2: "Expired",
      },
    },
    actions: {
      data: record.id || record.uuid,
      variant: "text",
    },
  }));

  const action = {
    isSystemGenerated: () => false,
    view: {
      handler: (item) => {
        // View details - handled by history modal
        setShowHistory(true);
      },
      show: true,
    },
  };

  if (loading || loadingSalary) {
    return (
      <div className="flex flex-col bg-white rounded-md h-full">
        <div className="border-b flex justify-between p-4">
          <h2 className="text-lg font-semibold text-theme">Salary Information</h2>
        </div>
        <div className="p-4">
          <div className="animate-pulse space-y-4">
            <div className="h-20 bg-primary-100 rounded"></div>
            <div className="h-32 bg-primary-100 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-white rounded-md h-full">
      <div className="border-b flex justify-between p-4">
        <h2 className="text-lg font-semibold text-theme">Salary Information</h2>
        <div className="flex gap-2">
          <DefaultButton
            variant="secondary"
            name="View History"
            onClick={() => setShowHistory(true)}
            classnames="text-sm"
          />
          {/* <DefaultButton
            variant="secondary"
            icon={<HiTrendingUp />}
            name="Increment History"
            onClick={() => setShowIncrementHistory(true)}
            classnames="text-sm"
          /> */}
          {/* <DefaultButton
            variant="outline"
            name="Audit Log"
            onClick={() => setShowAuditLog(true)}
            classnames="text-sm"
          /> */}
          <DefaultButton
            variant="default"
            name={currentSalary ? "Edit Salary" : "Assign Salary"}
            onClick={() => setShowWizard(true)}
            classnames="text-sm"
          />
        </div>
      </div>

      <div className="p-4">
        {currentSalary ? (
          <div className="flex flex-col gap-4">
            {/* Current Assignment */}
            <div className="bg-primary-50 rounded-lg p-4 border border-borderColor">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <HiCurrencyRupee className="text-theme text-xl" />
                  <span className="text-sm font-medium text-theme">Current Assignment</span>
                </div>
                {getStatusBadge(currentSalary.status)}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-textLight mb-1">Structure</p>
                  <p className="text-sm font-semibold text-theme">
                    {currentSalary.structureName}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-textLight mb-1">Effective Date</p>
                  <div className="flex items-center gap-1">
                    <HiClock className="text-textLight text-xs" />
                    <p className="text-sm font-semibold text-theme">
                      {formatDate(currentSalary.effectiveDate)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-4 gap-3 mt-4">
                <div>
                  <p className="text-xs text-textLight mb-1">Gross Pay</p>
                  <p className="text-base font-semibold text-theme">
                    {formatCurrency(currentSalary.grossPay)}
                  </p>
                  <p className="text-xs text-textLight mt-1">Monthly</p>
                </div>
                <div>
                  <p className="text-xs text-textLight mb-1">Net Pay</p>
                  <p className="text-base font-semibold text-success-600">
                    {formatCurrency(currentSalary.netPay)}
                  </p>
                  <p className="text-xs text-textLight mt-1">Monthly</p>
                </div>
                <div>
                  <p className="text-xs text-textLight mb-1">Annual CTC</p>
                  <p className="text-base font-semibold text-theme">
                    {formatCurrency(currentSalary.ctc)}
                  </p>
                  <p className="text-xs text-textLight mt-1">Per Year</p>
                </div>
                <div>
                  <p className="text-xs text-textLight mb-1">Deductions</p>
                  <p className="text-base font-semibold text-error-600">
                    {formatCurrency(
                      currentSalary.grossPay - currentSalary.netPay
                    )}
                  </p>
                  <p className="text-xs text-textLight mt-1">Monthly</p>
                </div>
              </div>
            </div>

            {/* History Table */}
            {salaryHistory.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-theme mb-3">Salary History</h3>
              <CommonTable
                TableHeaderData={TableHeaderData}
                tabledata={tableData}
                  loading={loadingSalary}
                // action={action}
                showActions={true}
              />
            </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8 flex flex-col items-center justify-center">
            <HiCurrencyRupee className="text-4xl text-textLight mx-auto mb-3" />
            <p className="text-textLight mb-4">No salary structure assigned</p>
            <DefaultButton
              variant="default"
              name="Assign Salary Structure"
              onClick={() => setShowWizard(true)}
            />
          </div>
        )}
      </div>

      {/* Modals */}
      {showWizard && (
        <SalaryAssignWizard
          employeeId={staff?.id}
          employeeData={staff}
          currentSalary={currentSalary}
          onClose={() => setShowWizard(false)}
          onSave={async (newSalary) => {
            // Refresh salary data after save
            try {
              const currentSalaryData = await getCurrentSalary(staff.uuid);
              if (currentSalaryData) {
                setCurrentSalary({
                  id: currentSalaryData.id || currentSalaryData.uuid,
                  structureName: currentSalaryData.structure_name || currentSalaryData.structureName || currentSalaryData.salary_structure?.name || "N/A",
                  effectiveDate: currentSalaryData.effective_date || currentSalaryData.effectiveDate,
                  status: currentSalaryData.status || "active",
                  grossPay: currentSalaryData.gross_pay || currentSalaryData.grossPay || 0,
                  netPay: currentSalaryData.net_pay || currentSalaryData.netPay || 0,
                  ctc: currentSalaryData.annual_ctc || currentSalaryData.ctc || currentSalaryData.annualCTC || 0,
                  createdBy: currentSalaryData.created_by?.name || currentSalaryData.createdBy || "System",
                  createdAt: currentSalaryData.created_at || currentSalaryData.createdAt,
                });
              }
              
              // Refresh history
              const historyResponse = await getSalaryHistory(staff.uuid, { per_page: 50 });
              const historyData = historyResponse?.data || historyResponse || [];
              setSalaryHistory(historyData);
            } catch (error) {
              console.error("Failed to refresh salary data:", error);
            }
            
            setShowWizard(false);
            heandleFetchData?.();
          }}
        />
      )}

      {showHistory && (
        <SalaryHistoryModal
          employeeId={staff?.uuid}
          onClose={() => setShowHistory(false)}
        />
      )}

      {showIncrementHistory && (
        <SalaryIncrementHistory
          staffId={staff?.id || staff?.uuid}
          onClose={() => setShowIncrementHistory(false)}
        />
      )}

      {showAuditLog && (
        <AuditLogViewer
          employeeId={staff?.uuid}
          onClose={() => setShowAuditLog(false)}
        />
      )}
    </div>
  );
};

export default SalaryCard;

